import { Gift, Lock } from "lucide-react";

import type { DisplayLoyaltyLadderTierReward, DisplayLoyaltyTier } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";

import { nextTierCopy } from "./loyaltyProgress";
import { LoyaltyTierBadge } from "./LoyaltyTierBadge";
import type { EnabledLoyaltyStatus } from "./useLoyaltyStatus";

type Translate = ReturnType<typeof useTranslation>["t"];

/** "In the app · 2 times", "At the clinic · Unlimited". */
const tierRewardTerms = (t: Translate, reward: DisplayLoyaltyLadderTierReward) => {
  const where = reward.isInOffice ? t("loyalty.tierRewardInOffice") : t("loyalty.tierRewardInApp");
  const limit =
    typeof reward.maxRedemptionsPerClient === "number"
      ? t("loyalty.tierRewardTimes", { count: reward.maxRedemptionsPerClient })
      : t("loyalty.tierRewardUnlimited");
  return `${where} · ${limit}`;
};

const rewardTitle = (reward: DisplayLoyaltyLadderTierReward) => {
  if (!reward.title) {
    throw new Error(`Tier reward ${reward.rewardId} has no title`);
  }
  return reward.title;
};

/** How the patient unlocks a tier: what is left to the next one, the threshold for the ones past it. */
const unlockCopy = (t: Translate, status: EnabledLoyaltyStatus, tier: DisplayLoyaltyTier) =>
  status.nextTier?.id === tier.id
    ? nextTierCopy(t, status)
    : t("loyalty.unlocksAtVisits", { count: tier.qualifyAtVisits });

/**
 * Every tier the patient has not reached, with what it unlocks: its public description and its
 * tier rewards, each with where it is used and how often. Members-only text stays with the tier
 * they hold, which the API sends on no other tier.
 */
export const LockedTiers = ({ status }: { readonly status: EnabledLoyaltyStatus }) => {
  const { t } = useTranslation();
  const lockedSteps = status.ladder.filter(({ isReached }) => !isReached);
  if (lockedSteps.length === 0) return null;

  return (
    <section
      aria-label={t("loyalty.tiersAhead")}
      className="flex flex-col gap-3 rounded-2xl bg-white p-5 shadow-sm"
    >
      <h2 className="text-sub1 text-primary">{t("loyalty.tiersAhead")}</h2>
      <ul className="flex flex-col divide-y divide-light-grey">
        {lockedSteps.map(({ tier, tierRewards }) => (
          <li key={tier.id} aria-label={tier.name} className="flex items-start gap-3 py-4">
            <LoyaltyTierBadge tier={tier} size="md" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-body1 font-semibold text-primary">{tier.name}</span>
                <span className="flex items-center gap-1 rounded-full bg-background-two px-2 py-0.5 text-body3 text-secondary">
                  <Lock aria-hidden className="h-3 w-3" />
                  {t("loyalty.lockedTier")}
                </span>
              </div>
              <p className="text-body2 text-secondary">{unlockCopy(t, status, tier)}</p>
              {tier.description && <p className="text-body2 text-primary">{tier.description}</p>}
              {tierRewards.length > 0 && (
                <ul
                  aria-label={t("loyalty.tierRewardsOf", { tier: tier.name })}
                  className="flex flex-col gap-1 pt-1"
                >
                  {tierRewards.map((reward) => (
                    <li key={reward.rewardId} className="flex items-start gap-2">
                      <Gift aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                      <span className="flex min-w-0 flex-col">
                        <span className="text-body2 text-primary">{rewardTitle(reward)}</span>
                        <span className="text-body3 text-secondary">
                          {tierRewardTerms(t, reward)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
